import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUser } from '../lib/api';
import type { ApiUser } from '../lib/api';

interface AuthContextType {
  user: ApiUser | null;
  loading: boolean;
  initialized: boolean;
  setUser: (u: ApiUser | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  initialized: false,
  setUser: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    const initAuth = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (mounted) {
          setUser(currentUser);
          setInitialized(true);
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ 認証エラー:', error);
        if (mounted) {
          setUser(null);
          setInitialized(true);
          setLoading(false);
        }
      }
    };
    initAuth();
    return () => { mounted = false; };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, initialized, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
